import posthog from 'posthog-js';
import { getParameterByName } from 'src/utils';
import redactSensitiveUrl, { redactDeep } from 'src/utils/redactSensitiveUrl';

const POSTHOG_KEY = 'phc_ClFltjX2uUtB5S4TTlY9ZOr5CTuJhe4hVOLOcS4rOSC';
const POSTHOG_HOST = 'https://us.i.posthog.com';

let initialized = false;

/**
 * Last stop before anything leaves for PostHog: the photobook bio link token
 * is a credential, and PostHog fills $current_url, $pathname, $referrer and
 * their $initial_* twins from the address bar on its own. The token is
 * normally gone from the URL before PostHog starts (captureBioTokenOnLoad),
 * so this is the safety net. Replay snapshots are left to the recorder's own
 * URL hook below, because walking a full DOM snapshot here would be slow.
 */
export function redactCaptureResult(result) {
  if (!result) return result;

  const out = { ...result };
  if (result.event === '$snapshot') {
    const props = { ...(result.properties || {}) };
    Object.keys(props).forEach((key) => {
      if (typeof props[key] === 'string') props[key] = redactSensitiveUrl(props[key]);
    });
    out.properties = props;
  } else {
    out.properties = redactDeep(result.properties);
  }
  if (result.$set) out.$set = redactDeep(result.$set);
  if (result.$set_once) out.$set_once = redactDeep(result.$set_once);

  return out;
}

/** Replay: the recorder runs page URLs and network request URLs through this. */
export function redactRecordedRequest(request) {
  if (!request) return request;
  const out = { ...request };
  if (typeof out.name === 'string') out.name = redactSensitiveUrl(out.name);
  if (typeof out.url === 'string') out.url = redactSensitiveUrl(out.url);

  return out;
}
const pendingEvents = [];
const MAX_PENDING = 50;
// Session restore can resolve /users/me before initAnalytics() runs (it waits on
// an untimed ad-block probe) — park the identify and flush it on init, or the
// restored user silently stays anonymous for the whole session.
let pendingIdentify = null;

/**
 * Initialize PostHog with autocapture OFF to control event volume.
 * Call once at app startup (production only).
 */
export function initAnalytics() {
  if (initialized) return;
  if (process.env.REACT_APP_PRODUCTION !== 'TRUE') return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: false,
    capture_pageview: false,
    // Ingest cost: no insight uses $pageleave (checked 2026-07); GA4 covers
    // bounce/engagement. ~205K events/mo.
    capture_pageleave: false,
    capture_performance: false,
    // Ingest cost: Sentry owns ambient error tracking. Manual captureException()
    // below still lands in PostHog for deliberately instrumented flows. ~75K/mo.
    capture_exceptions: false,
    disable_session_recording: false,
    // Pins the SDK default: anonymous visitors are billed at the anonymous rate
    // (~1/5 of identified) and only get a person profile after identify().
    person_profiles: 'identified_only',
    persistence: 'localStorage+cookie',
    cross_subdomain_cookie: true,
    before_send: redactCaptureResult,
    session_recording: {
      maskCapturedNetworkRequestFn: redactRecordedRequest,
    },
    loaded(ph) {
      // Disable automatic collection of PII in properties
      ph.set_config({ sanitize_properties: null });
    },
  });

  initialized = true;

  // Identify before flushing queued events so they attribute to the person.
  if (pendingIdentify) {
    const user = pendingIdentify;
    pendingIdentify = null;
    identifyUser(user);
  }
  pendingEvents.splice(0).forEach(([name, props]) => posthog.capture(name, props));
}

// UTC day stamp (e.g. "2026-07-13") — matches PostHog's UTC day buckets.
function utcDay() {
  return new Date().toISOString().slice(0, 10);
}

const IDENTIFY_DAY_KEY = 'ph_identify_day';

/**
 * Identify a logged-in user. Call on login, registration, and session restore.
 * Merges anonymous events into the identified user profile.
 *
 * Ingest cost: re-identifying an already-identified user emits a $set event
 * (person-property refresh) billed at the identified rate — and this fires on
 * every app load (~205K/mo). Throttled to once per user per UTC day. A FRESH
 * identify (current distinct_id differs from the user id — login after logout,
 * new device) always goes through, or the anonymous→person merge would be lost.
 * Trade-off: person properties (wallet_balance, has_pets, …) may be up to 24h
 * stale in PostHog.
 */
export function identifyUser(user) {
  if (!user) return;
  if (!initialized) {
    pendingIdentify = user;
    return;
  }

  const userId = String(user.id);
  const stamp = `${userId}:${utcDay()}`;
  let alreadyIdentifiedToday = false;
  try {
    alreadyIdentifiedToday = posthog.get_distinct_id() === userId
      && localStorage.getItem(IDENTIFY_DAY_KEY) === stamp;
  } catch (e) { /* */ }
  if (alreadyIdentifiedToday) return;

  posthog.identify(userId, {
    email: user.email || undefined,
    name: user.nickname || undefined,
    country: user.countryCode || undefined,
    has_pets: Boolean(user.isAnyPets),
    wallet_balance: user.wallet?.amount || 0,
    referral_code: user.referral?.code || undefined,
  });
  // Reload flags after identify so email-based targeting works
  posthog.reloadFeatureFlags();
  try { localStorage.setItem(IDENTIFY_DAY_KEY, stamp); } catch (e) { /* */ }
}

/**
 * Reset identity on logout. Creates a new anonymous ID for future events.
 */
export function resetAnalytics() {
  pendingIdentify = null;
  if (!initialized) return;
  posthog.reset();
  // Clear experiment caches so the next user gets fresh bucketing.
  // pricing: sessionStorage; onboarding: localStorage (must survive auth redirects).
  try { sessionStorage.removeItem('pupvote_pricing_variant'); } catch (e) { /* */ }
  try { localStorage.removeItem('pupvote_onboarding_variant'); } catch (e) { /* */ }
  // Clear the once-per-session exposure marker so the next user in this tab is
  // re-bucketed and re-exposed cleanly.
  try { sessionStorage.removeItem('onboarding_exposed'); } catch (e) { /* */ }
}

const PAGEVIEW_DAY_KEY = 'ph_pv_day';

/**
 * Track a page view with structured properties.
 *
 * Ingest cost: identified users' events are billed at ~5x the anonymous rate,
 * and identified $pageview is the single biggest PostHog line item (~880K/mo).
 * For identified users only the FIRST pageview per UTC day is captured — enough
 * to keep DAU/retention/return analyses exact. Anonymous visitors keep full
 * pageview tracking (near-free, powers the onboarding entry funnel and SEO
 * insights). Page-level trails for logged-in users live in GA4; pages that need
 * PostHog-side analysis should fire a custom event via trackEvent().
 */
export function trackPageView(path) {
  if (!initialized) return;

  let dedupStamp = null;
  try {
    const distinctId = posthog.get_distinct_id();
    // We only ever identify with the numeric String(user.id); anonymous IDs are UUIDs.
    if (/^\d+$/.test(distinctId)) {
      dedupStamp = `${distinctId}:${utcDay()}`;
      if (localStorage.getItem(PAGEVIEW_DAY_KEY) === dedupStamp) return;
    }
  } catch (e) { /* dedup unavailable — fall through and capture */ }

  const pageType = getPageType(path);

  posthog.capture('$pageview', {
    $current_url: window.location.href,
    page_type: pageType,
    locale: getLocale(path),
  });

  // Stamp only after a successful capture so a capture failure can't
  // permanently eat the day's one identified pageview.
  if (dedupStamp) {
    try { localStorage.setItem(PAGEVIEW_DAY_KEY, dedupStamp); } catch (e) { /* */ }
  }
}

// --- PostHog ingest cost control ---
//
// High-volume debugging funnels muted client-side once their feature settled, to
// keep PostHog ingest in check. All custom events flow through trackEvent(), so
// this is the single chokepoint. To re-open a funnel for analysis, relax the rule
// here — the call sites still fire, they're just dropped before capture.
//
//   Onboarding redesign — shipped at 100% (experiment won). Keep only the funnel
//     endpoints: onboarding_entry (start) and pet_created (finish, fired
//     separately). Every intermediate onboarding_* step is dropped.
//   Rewarded video / post-vote ad — fully rolled out and settled. Keep the
//     minimal health funnel: invitation accepted → ad completed → SSV confirmed
//     (the revenue event). Every other rewarded_ad_* / post_vote_ad_* is dropped.
//   Concluded experiments — $experiment_started exposure is dropped for flags
//     that have finished rolling out (or been killed). A NEW experiment still
//     tracks until its flag key is added to SETTLED_EXPERIMENTS below.
//
// Further levers live at their sites: $pageleave + $exception autocapture off
// (init config), identify/$set throttled to once/day (identifyUser), identified
// $pageview deduped to first-of-day (trackPageView). Identified events bill at
// ~5x the anonymous rate, so cuts to identified volume are what move the bill.
const REWARDED_AD_KEEP = new Set([
  'post_vote_ad_invitation_accepted',
  'rewarded_ad_completed',
  'rewarded_ad_ssv_confirmed',
]);

const SETTLED_EXPERIMENTS = new Set([
  'vote-modal-redesign', // rolled out 100% redesign
  'pricing-experiment', // concluded (flag disabled; entry price points kept)
  'onboarding-redesign', // rolled out 100% treatment
  'rewarded-video-skip', // rolled out 100% treatment
]);

function isMutedEvent(name, properties) {
  // Onboarding: keep only the entry event; drop every intermediate step.
  if (name.startsWith('onboarding_') && name !== 'onboarding_entry') return true;
  // Rewarded-video / post-vote-ad: keep only accepted → completed → ssv_confirmed.
  if (
    (name.startsWith('rewarded_ad_') || name.startsWith('post_vote_ad_'))
    && !REWARDED_AD_KEEP.has(name)
  ) {
    return true;
  }
  // Exposure for experiments that have concluded — no live split left to analyze.
  if (name === '$experiment_started' && SETTLED_EXPERIMENTS.has(properties && properties.$feature_flag)) {
    return true;
  }
  return false;
}

/**
 * Track a custom event. All events should go through this function.
 */
export function trackEvent(eventName, properties = {}) {
  if (isMutedEvent(eventName, properties)) return;
  if (!initialized) {
    if (pendingEvents.length < MAX_PENDING) {
      pendingEvents.push([eventName, properties]);
    }
    return;
  }
  posthog.capture(eventName, properties);
}

/**
 * Get the PostHog instance for feature flags, etc.
 */
export function getPostHog() {
  if (!initialized) return null;
  return posthog;
}

// Current anonymous/identified distinct_id, or null when PostHog isn't ready.
export function getDistinctId() {
  return initialized ? posthog.get_distinct_id() : null;
}

/**
 * Manually capture an exception. Use in catch blocks for critical flows.
 */
export function captureException(error, properties = {}) {
  if (!initialized) return;
  posthog.capture('$exception', {
    $exception_message: error?.message || String(error),
    $exception_type: error?.name || 'Error',
    $exception_stack_trace_raw: error?.stack || '',
    ...properties,
  });
}

// --- Convenience trackers for specific events ---

export function trackSignup(method) {
  trackEvent('signup_completed', { method });
}

export function trackLogin(method) {
  trackEvent('login_completed', { method });
}

export function trackFbHashDetected() {
  trackEvent('fb_oauth_hash_detected');
}

export const FB_OAUTH_FAILURE = Object.freeze({
  BLOCKED_OR_DELETED: 'blocked_or_deleted',
  INVALID_REQUEST: 'invalid_request',
  SERVER_ERROR: 'server_error',
  NETWORK_OR_CLIENT_ERROR: 'network_or_client_error',
});

export function trackFbOauthFailed(reason, status) {
  trackEvent('fb_oauth_login_failed', { reason, status });
}

export function trackPetCreated(petType, breed, hasContest) {
  trackEvent('pet_created', {
    pet_type: petType,
    breed: breed || undefined,
    joined_contest: hasContest,
  });
}

export function trackFreeVote(petId, petType) {
  trackEvent('free_vote_cast', {
    pet_id: petId,
    pet_type: petType || undefined,
  });
}

export function trackVotePopupOpened(petId) {
  trackEvent('vote_popup_opened', { pet_id: petId });
}

export function trackVotePackageSelected(packageId, votes, price, currency) {
  trackEvent('vote_package_selected', {
    package_id: packageId,
    votes,
    price,
    currency: currency || 'USD',
  });
}

export function trackPaymentMethodSelected(method) {
  trackEvent('payment_method_selected', { method });
}

export function trackPurchaseCompleted(amount, votes, method, petId, currency) {
  // Normalize amount: accept formatted strings like "$5.49", numbers, or null
  const numericAmount = amount != null
    ? parseFloat(String(amount).replace(/[^\d.-]/g, '')) || null
    : null;
  trackEvent('purchase_completed', {
    amount: numericAmount,
    votes,
    method,
    pet_id: petId,
    currency: currency || 'USD',
  });
}

export function trackPurchaseFailed(reason, amount) {
  trackEvent('purchase_failed', {
    reason: reason || 'unknown',
    amount,
  });
}

export function trackDepositCompleted(amount) {
  trackEvent('deposit_completed', { amount });
}

export function trackDepositFailed(reason) {
  trackEvent('deposit_failed', { reason: reason || 'unknown' });
}

export function trackVoteModalExperiment(variant) {
  trackEvent('$experiment_started', {
    $feature_flag: 'vote-modal-redesign',
    $feature_flag_response: variant,
  });
}

export function trackPricingExperiment(variant) {
  trackEvent('$experiment_started', {
    $feature_flag: 'pricing-experiment',
    $feature_flag_response: variant,
  });
}

// --- Onboarding (progressive add-pet) experiment funnel ---
//
// Exposure is bucketed for anonymous Home visitors (the ad landing). Per-step
// drop-off funnel in PostHog (filter onboarding_step_viewed by the `step` prop):
//   onboarding_entry → $experiment_started
//     → onboarding_step_viewed{step=type} → {name} → {breed} → {region} → {bio}
//     → {account} → {photo} → {done}
//     → pet_created
// onboarding_step_completed{step} (advanced past a step) + account_prompt_shown /
// account_created / pet_form_shown / pet_submitted remain for within-step detail.
//
// Common props (variant, entry_surface, pet_type) are attached to every event so
// each arm's per-step leak is comparable. Keep them stable — dashboards depend.

function onboardingCommonProps(extra = {}) {
  return {
    variant: extra.variant || undefined,
    entry_surface: extra.entry_surface || undefined,
    pet_type: extra.pet_type || undefined,
    ...extra,
  };
}

export function trackOnboardingExperiment(variant) {
  trackEvent('$experiment_started', {
    $feature_flag: 'onboarding-redesign',
    $feature_flag_response: variant,
  });
}

export function trackOnboardingEntry(props) {
  trackEvent('onboarding_entry', onboardingCommonProps(props));
}

// Fired when each step is SHOWN (one event, `step` property identifies which) —
// the primary drop-off signal. Build the PostHog funnel on this: a leak between
// onboarding_step_viewed{step=A} and {step=B} is the drop ON step A.
export function trackOnboardingStepViewed(step, props) {
  trackEvent('onboarding_step_viewed', onboardingCommonProps({ ...props, step }));
}

export function trackOnboardingStepCompleted(step, props) {
  trackEvent('onboarding_step_completed', onboardingCommonProps({ ...props, step }));
}

export function trackOnboardingAccountPromptShown(props) {
  trackEvent('onboarding_account_prompt_shown', onboardingCommonProps(props));
}

export function trackOnboardingAccountCreated(method, props) {
  trackEvent('onboarding_account_created', onboardingCommonProps({ ...props, method }));
}

export function trackOnboardingPetFormShown(props) {
  trackEvent('onboarding_pet_form_shown', onboardingCommonProps(props));
}

export function trackOnboardingPetSubmitted(props) {
  trackEvent('onboarding_pet_submitted', onboardingCommonProps(props));
}

// --- Lapsed-purchaser reactivation campaign funnel ---
//
// Build the conversion funnel in PostHog:
//   reactivation_landing_viewed → ..._spin_completed → ..._claim_succeeded → purchase_completed
// Break down by tier (utm_content) and cohort (utm_term) to compare segments.

function reactivationCommonProps(extra = {}) {
  return {
    tier: getParameterByName('utm_content') || undefined,
    cohort: parseCohort(),
    utm_campaign: getParameterByName('utm_campaign') || undefined,
    ...extra,
  };
}

export function trackReactivationLandingViewed(props) {
  trackEvent('reactivation_landing_viewed', reactivationCommonProps(props));
}

export function trackReactivationSpinStarted(props) {
  trackEvent('reactivation_spin_started', reactivationCommonProps(props));
}

export function trackReactivationSpinCompleted(props) {
  trackEvent('reactivation_spin_completed', reactivationCommonProps(props));
}

export function trackReactivationClaimAttempted(props) {
  trackEvent('reactivation_claim_attempted', reactivationCommonProps(props));
}

export function trackReactivationLoginRequired(props) {
  trackEvent('reactivation_login_required', reactivationCommonProps(props));
}

export function trackReactivationClaimSucceeded(props) {
  trackEvent('reactivation_claim_succeeded', reactivationCommonProps(props));
}

export function trackReactivationContinueClicked(props) {
  trackEvent('reactivation_continue_clicked', reactivationCommonProps(props));
}

// --- Rewarded video ad funnel ---
//
// Watch a Google rewarded video ad → Server-Side Verification webhook clears
// the user's free-vote cooldown → user casts another vote.
//
// Build the funnel in PostHog:
//   rewarded_ad_cta_shown → _cta_clicked → _nonce_requested → _loaded
//     → _completed → _ssv_confirmed → free_vote_cast (with unlocked_by=rewarded_ad)
//
// Common props attached to every event let us slice by surface, device, and
// daily count. Keep these stable — dashboards depend on them.

function rewardedAdCommonProps(extra = {}) {
  let device = 'desktop';
  if (typeof window !== 'undefined') {
    if (window.innerWidth <= 480) device = 'mobile';
    else if (window.innerWidth <= 767) device = 'tablet';
  }
  return {
    pet_id: extra.pet_id || undefined,
    surface: extra.surface || undefined, // 'popup' | 'detail_page'
    device,
    daily_count_so_far: extra.daily_count_so_far ?? undefined,
    ...extra,
  };
}

export function trackRewardedAdCtaShown(props) {
  trackEvent('rewarded_ad_cta_shown', rewardedAdCommonProps(props));
}

export function trackRewardedAdCtaClicked(props) {
  trackEvent('rewarded_ad_cta_clicked', rewardedAdCommonProps(props));
}

export function trackRewardedAdNonceRequested(props) {
  trackEvent('rewarded_ad_nonce_requested', rewardedAdCommonProps(props));
}

export function trackRewardedAdLoaded(props) {
  trackEvent('rewarded_ad_loaded', rewardedAdCommonProps(props));
}

export function trackRewardedAdLoadFailed(props) {
  // reason: 'timeout' | 'no_fill' | 'gpt_unavailable' | 'nonce_failed'
  trackEvent('rewarded_ad_load_failed', rewardedAdCommonProps(props));
}

export function trackRewardedAdCompleted(props) {
  trackEvent('rewarded_ad_completed', rewardedAdCommonProps(props));
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'Rewarded Ad Completed', pet_id: props?.pet_id });
  }
}

export function trackRewardedAdDismissed(props) {
  trackEvent('rewarded_ad_dismissed', rewardedAdCommonProps(props));
}

export function trackRewardedAdSsvConfirmed(props) {
  trackEvent('rewarded_ad_ssv_confirmed', rewardedAdCommonProps(props));
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'Rewarded Ad SSV Confirmed', pet_id: props?.pet_id });
  }
}

export function trackRewardedAdSsvTimeout(props) {
  trackEvent('rewarded_ad_ssv_timeout', rewardedAdCommonProps(props));
}

export function trackRewardedAdCapReached(props) {
  trackEvent('rewarded_ad_cap_reached', rewardedAdCommonProps(props));
}

export function trackRewardedAdBlocked(props) {
  trackEvent('rewarded_ad_blocked', rewardedAdCommonProps(props));
}

export function trackRewardedAdExperiment(variant) {
  trackEvent('$experiment_started', {
    $feature_flag: 'rewarded-video-skip',
    $feature_flag_response: variant,
  });
}

// Post-vote ad invitation modal — surfaced after a successful free vote
// when eligibility passes. Funnel: shown → (accepted | shared_instead |
// dismissed) → (if accepted) reuses the rewarded_ad_* events for the rest.
export function trackPostVoteAdInvitationShown(props) {
  trackEvent('post_vote_ad_invitation_shown', rewardedAdCommonProps({ ...props, surface: 'post_vote_modal' }));
}

export function trackPostVoteAdInvitationAccepted(props) {
  trackEvent('post_vote_ad_invitation_accepted', rewardedAdCommonProps({ ...props, surface: 'post_vote_modal' }));
}

export function trackPostVoteAdInvitationSharedInstead(props) {
  trackEvent('post_vote_ad_invitation_shared_instead', rewardedAdCommonProps({ ...props, surface: 'post_vote_modal' }));
}

export function trackPostVoteAdInvitationDismissed(props) {
  trackEvent('post_vote_ad_invitation_dismissed', rewardedAdCommonProps({ ...props, surface: 'post_vote_modal' }));
}

export function trackReactivationNoPetShown(props) {
  trackEvent('reactivation_no_pet_shown', reactivationCommonProps(props));
}

export function trackReactivationAddPetClicked(props) {
  trackEvent('reactivation_add_pet_clicked', reactivationCommonProps(props));
}

function parseCohort() {
  const term = getParameterByName('utm_term');
  const m = term && term.match(/^cohort_(\d+)$/);
  return m ? Number(m[1]) : undefined;
}

export function setPricingVariantProperty(variant) {
  if (!initialized) return;
  if (variant) {
    posthog.register({ pricing_variant: variant });
  } else {
    posthog.unregister('pricing_variant');
  }
}

// --- Helpers ---

function getLocale(path) {
  const parts = (path || window.location.pathname).split('/').filter(Boolean);
  return parts[0] || 'us';
}

function getPageType(path) {
  const parts = (path || window.location.pathname).split('/').filter(Boolean);
  const route = parts[1] || 'home';

  const routeMap = {
    home: 'home',
    detail: 'pet_detail',
    pets: 'my_pets',
    pet: 'add_pet',
    contest: 'contest',
    leaderboard: 'leaderboard',
    tournament: 'tournament',
    challenges: 'challenges',
    challenge: 'challenge',
    photos: 'photos',
    streaks: 'streaks',
    pupswipe: 'pupswipe',
    deposit: 'deposit',
    withdraw: 'withdraw',
    referrals: 'referrals',
    achievements: 'achievements',
    breeds: 'breeds',
    search: 'search',
    help: 'help',
    compare: 'breed_comparison',
    'cutest-dogs': 'gallery_dogs',
    'cutest-cats': 'gallery_cats',
    'cute-pet-pictures': 'gallery_all',
    account: 'account',
  };

  return routeMap[route] || route;
}
